import React, { useState, useEffect, useRef } from "react";
import { FaDownload, FaUpload, FaTrash, FaEdit, FaUndo } from "react-icons/fa";
import axios from "axios";
import "./Vehicle.css";
import config from "../../config";

const { useAuth } = require("../../Auth/AuthContext");

const getVehicleDataUrl = `${config.BASE_URL}/list-vehicle`;
const insertVehicleDataUrl = `${config.BASE_URL}/add-vehicle`;
const uploadDocumentUrl = `${config.BASE_URL}/upload`;
const deleteVehicleDataUrl = `${config.BASE_URL}/delete-vehicle`;
const resetVehicleDocUrl = `${config.BASE_URL}/reset-vehicle-doc`;

const Vehicle = () => {
    const [formData, setFormData] = useState({
        truck_no: "",
        make: "",
        companyOwner: "",
        fields: [
            { name: "registration", file: null, startDate: "", endDate: "" },
            { name: "insurance", file: null, startDate: "", endDate: "" },
            { name: "fitness", file: null, startDate: "", endDate: "" },
            { name: "mv_tax", file: null, startDate: "", endDate: "" },
            { name: "puc", file: null, startDate: "", endDate: "" },
            { name: "ka_tax", file: null, startDate: "", endDate: "" },
            { name: "basic_and_KA_permit", file: null, startDate: "", endDate: "" },
        ],
    });
    const fileInputRef = useRef([]);
    const [result, setResult] = useState([]);
    const [submitMessage, setSubmitMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [uploadFields, setUploadFields] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [activeUploadField, setActiveUploadField] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [fileErrors, setFileErrors] = useState("");
    const [progress, setProgress] = useState({});
    const [dateError, setDateError] = useState("");
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        recordId: null,
    });

    const { activeModule, setActiveModule } = useAuth();

    const keyDisplayMap = {
        registration: "Registration",
        insurance: "Insurance",
        fitness: "Fitness",
        mv_tax: "MV Tax",
        puc: "PUC",
        ka_tax: "KA Tax",
        basic_and_KA_permit: "Basic & KA Permit",
    }


    // Add to your existing state declarations
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [editFormData, setEditFormData] = useState({
        truck_no: "",
        make: "",
        companyOwner: "",
        fields: []
    });
    const [updateDataError, setUpdateDataError] = useState({});
    const [fieldSpecificErrors, setFieldSpecificErrors] = useState({});
    // reset states
    const [resetModal, setResetModal] = useState({
        isOpen: false,
        truckNo: null,
        docKey: null
    });

    useEffect(() => {
        sessionStorage.setItem('actMod', 'vehicle');
        setActiveModule(sessionStorage.getItem('actMod'));

    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get(getVehicleDataUrl, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                }
            });

            if (response?.data && Array.isArray(response.data)) {
                // Transform null values to empty strings
                const cleanedData = response.data.map(vehicle => ({
                    ...vehicle,
                    companyOwner: vehicle.companyOwner !== null ? vehicle.companyOwner : "",
                    make: vehicle.make !== null ? vehicle.make : ""
                }));
                setResult(cleanedData);
            }
        } catch (error) {
            console.error("Error fetching vehicles data:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "truck_no") {
            // Convert to uppercase and remove spaces
            const truckNo = value.toUpperCase().replace(/\s+/g, '');

            // Define the fixed pattern for truck numbers
            const truckNoPattern = /^[A-Z]{2}\d{2}[A-Z]{2}\d{4}$/;

            if (!truckNoPattern.test(truckNo)) {
                setErrorMessage("Use format: MH12KL1111");
                setFormData({ ...formData, [name]: truckNo }); // Update input field
                return; // Stop further execution if format is incorrect
            }

            // Check if the entered truck_no already exists in filteredVehicles
            const check_truck = filteredVehicles.some(vehicle => vehicle.truck_no.toUpperCase() === truckNo);

            if (check_truck) {
                setErrorMessage("Truck already exists.");
            } else {
                setErrorMessage("");
            }

            setFormData({ ...formData, [name]: truckNo });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleFieldChange = (index, field, value) => {
        setFormData((prevState) => {
            const updatedFields = [...prevState.fields];

            if (field === "file" && value) {
                const fieldId = `field-${index}`;
                const fileInput = document.getElementById(fieldId);

                if (value.type && value.type !== "application/pdf") {
                    setFileErrors((prev) => ({ ...prev, [fieldId]: "File type should be .pdf only" }));
                    if (fileInput) fileInput.value = "";
                    return prevState; // Prevent further state updates
                }

                if (value.size && value.size > 1 * 1024 * 1024) {
                    setFileErrors((prev) => ({ ...prev, [fieldId]: "File size should be 1MB or less" }));
                    if (fileInput) fileInput.value = "";
                    return prevState;
                }

                setFileErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldId];
                    return newErrors;
                });

                updatedFields[index] = { ...updatedFields[index], file: value };
            }

            if (field === "startDate" || field === "endDate") {
                updatedFields[index] = { ...updatedFields[index], [field]: value };

                const startDate = updatedFields[index].startDate;
                const endDate = updatedFields[index].endDate;

                if ((startDate && !endDate) || (!startDate && endDate)) {
                    setDateError((prev) => ({ ...prev, [`field-${index}`]: "Both start and end dates should be provided." }));
                } else if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
                    setDateError((prev) => ({ ...prev, [`field-${index}`]: "End date should be greater than or equal to the start date." }));
                } else {
                    setDateError((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors[`field-${index}`];
                        return newErrors;
                    });
                }
            }

            return {
                ...prevState,
                fields: updatedFields,
            };
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setProgress({});

        let isValid = true;
        const errors = {};

        formData.fields.forEach((field, index) => {
            const fieldId = `field-${index}`;
            const hasFile = !!field.file;
            const hasStartDate = !!field.startDate;
            const hasEndDate = !!field.endDate;

            // Case 1: If a file is uploaded, both dates must be provided
            if (hasFile && (!hasStartDate || !hasEndDate)) {
                errors[fieldId] = "Both start and end dates are required when a file is uploaded.";
                isValid = false;
            }

            // Case 2: If either date is provided, a file must be uploaded
            if ((hasStartDate || hasEndDate) && !hasFile) {
                errors[fieldId] = "A file must be uploaded when start and end dates are provided.";
                isValid = false;
            }

            // If all three are present, remove any existing error
            if (hasFile && hasStartDate && hasEndDate) {
                delete errors[fieldId];
            }
        });

        if (!isValid) {
            setDateError(errors);
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("truck_no", formData.truck_no.trim());
        formDataToSend.append("make", formData.make);
        formDataToSend.append("companyOwner", formData.companyOwner);

        const fieldNames = {};
        formData.fields.forEach((f) => {
            if (f.file) {
                formDataToSend.append(f.name, f.file);
                fieldNames[f.name] = 0;
            }
            formDataToSend.append(`${f.name}_startDate`, f.startDate || "");
            formDataToSend.append(`${f.name}_endDate`, f.endDate || "");
        });

        try {
            const response = await axios.post(insertVehicleDataUrl, formDataToSend,
                {
                    withCredentials: true,
                    headers:
                    {
                        "Content-Type": "multipart/form-data"
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgress((prevProgress) => ({
                            ...prevProgress,
                            ...Object.keys(fieldNames).reduce((acc, name) => {
                                acc[name] = percentCompleted;
                                return acc;
                            }, {}),
                        }));
                    },
                });

            console.log("Response:", response.data);
            setSubmitMessage("Vehicle Details added successfully");

            setTimeout(() => {
                setSubmitMessage("");
            }, 3000);

            setProgress({});

            setFormData({
                truck_no: "",
                make: "",
                companyOwner: "",
                fields: formData.fields.map((field) => ({
                    ...field,
                    startDate: "",
                    endDate: "",
                    file: null,
                })),
            });
            fileInputRef.current.forEach((input) => {
                if (input) input.value = "";
            });

            fetchData();
        } catch (error) {
            console.error("Error submitting data:", error);
            setSubmitMessage("Error: Adding vehicle details failed");
            setProgress({});
        }
    };

    const handleUploadClick = (truckNo, fieldName) => {
        const fieldKey = `${truckNo}-${fieldName}`;
        setActiveUploadField((prev) => (prev === fieldKey ? null : fieldKey));
    };

    // Update the click outside handler to keep the active upload field open if the click is inside
    const handleClickOutside = (e) => {
        const uploadFields = document.querySelectorAll('.upload-fields');

        const isClickInside = Array.from(uploadFields).some((field) => field.contains(e.target));

        if (!isClickInside) {
            setActiveUploadField(null);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside); // Use 'mousedown' for better outside detection
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleUploadSubmit = async (truckNo, fieldName, file, startDate, endDate) => {
        const formData = new FormData();
        formData.append("truck_no", truckNo);
        formData.append("field_name", fieldName);
        formData.append("file", file);
        formData.append("start_date", startDate);
        formData.append("end_date", endDate);

        try {
            await axios.post(uploadDocumentUrl, formData,
                {
                    withCredentials: true,
                    headers:
                    {
                        "Content-Type": "multipart/form-data"
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setProgress((prev) => ({
                            ...prev,
                            [`${truckNo}-${fieldName}`]: percentCompleted,
                        }));
                    },
                });

            alert("File uploaded successfully!");

            setProgress((prev) => {
                console.log("Resetting Progress State:", { ...prev, [`${truckNo}-${fieldName}`]: 0 });
                return { ...prev, [`${truckNo}-${fieldName}`]: 0 };
            });

            setUploadFields((prev) => ({ ...prev, [`${truckNo}-${fieldName}`]: false }));

            fetchData();
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Error uploading file. Please try again.");
        }
    };

    const handleFileChange = (e, truckNo, key) => {
        const file = e.target.files[0];
        const fieldId = `${truckNo}-${key}-file`;

        if (file && file.type !== "application/pdf") {
            setFileErrors((prev) => ({ ...prev, [fieldId]: "File type should be .pdf only" }));
            e.target.value = ""; // Reset file input
            return;
        }

        if (file.size > 1 * 1024 * 1024) {
            setFileErrors((prev) => ({ ...prev, [fieldId]: "File size should be 1MB or less" }));
            e.target.value = ""; // Reset file input
            return;
        }

        setFileErrors((prev) => ({ ...prev, [fieldId]: "" }));

        setSelectedFile(file);
        setStartDate(document.getElementById(`${truckNo}-${key}-start`).value);
        setEndDate(document.getElementById(`${truckNo}-${key}-end`).value);

        setProgress((prev) => {
            console.log("Initialized Progress State:", { ...prev, [`${truckNo}-${key}`]: 0 });
            return { ...prev, [`${truckNo}-${key}`]: 0 };
        });
    };

    const handleStartDateChange = (e, endDate, truckNo, key) => {
        const startDate = e.target.value;
        const fieldId = `${truckNo}-${key}`;

        if (new Date(endDate) < new Date(startDate)) {
            setDateError((prev) => ({ ...prev, [fieldId]: "Start date should be lesser than or equal to the end date." }));
            setStartDate(""); // Clear start date
            return;
        }

        setDateError((prev) => ({ ...prev, [fieldId]: "" }));
        setStartDate(startDate); // Update the start date state
    };

    const handleEndDateChange = (e, startDate, truckNo, key) => {
        const endDate = e.target.value;
        const fieldId = `${truckNo}-${key}`;

        if (new Date(endDate) < new Date(startDate)) {
            setDateError((prev) => ({ ...prev, [fieldId]: "End date should be greater than or equal to the start date." }));
            setEndDate(""); // Clear end date
            return;
        }

        setDateError((prev) => ({ ...prev, [fieldId]: "" }));
        setEndDate(endDate); // Update the end date state
    };

    const handleSaveClick = (truckNo, key) => {
        const fieldId = `${truckNo}-${key}`;
        const startDate = document.getElementById(`${truckNo}-${key}-start`).value;
        const endDate = document.getElementById(`${truckNo}-${key}-end`).value;

        // Check for file upload
        if (!selectedFile) {
            setFileErrors("Please select a file to upload.");
            setActiveUploadField(fieldId);
            return;
        }

        // Set active upload field only if the file is uploaded but dates are missing
        if (!startDate || !endDate) {
            setDateError((prev) => ({ ...prev, [fieldId]: "Both start and end dates are required." }));
            return;
        }

        if (new Date(endDate) < new Date(startDate)) {
            setDateError((prev) => ({ ...prev, [fieldId]: "End date should be greater than or equal to the start date." }));
            return;
        }

        // Reset progress state
        setProgress((prev) => {
            console.log("Reset Progress State:", { ...prev, [`${truckNo}-${key}`]: 0 });
            return { ...prev, [`${truckNo}-${key}`]: 0 };
        });

        // Upload file
        handleUploadSubmit(
            truckNo,
            key,
            selectedFile,
            startDate,
            endDate
        );

        // Reset states after upload
        setActiveUploadField(null);
        setSelectedFile(null);
        setStartDate('');
        setEndDate('');
    };

    const filteredVehicles = result && Array.isArray(result) ? result.filter(vehicle => {
        const truckNo = vehicle.truck_no.toLowerCase();
        const search = searchTerm.toLowerCase();

        // return [...search].every(char => truckNo.includes(char));
        return truckNo.includes(search);
    }) : [];
    const handleDelete = async (truck_no) => {
        setDeleteModal({
            isOpen: true,
            recordId: truck_no,
        });
    }

    const confirmDelete = async () => {
        try {
            const response = await axios.delete(`${deleteVehicleDataUrl}/${deleteModal.recordId}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                }
            });
            if (response.status === 200) {
                console.log("Vehicle deleted successfully:", deleteModal.recordId);
                alert("Vehicle deleted successfully!");
                fetchData(); // Refresh the data
            } else {
                alert("Failed to delete Vehicle.");
            }
            setDeleteModal({ isOpen: false, recordId: null });
            // Show success notification (you could add a toast notification system here)
        } catch (error) {
            console.error('Error deleting Vehicle:', error);

            let errorMessage = 'Error deleting Vehicle';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            alert(errorMessage);
        }
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB'); // dd/mm/yyyy format
    };

    const handleResetClick = (truckNo, docKey) => {
        setResetModal({
            isOpen: true,
            truckNo: truckNo,
            docKey: docKey
        });
    };

    const confirmReset = async () => {
        try {
            const response = await axios.post(resetVehicleDocUrl, {
                truck_no: resetModal.truckNo,
                field_name: resetModal.docKey
            }, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                }
            });

            if (response.status === 200) {
                alert("Document reset successfully!");
                fetchData(); // Refresh the data
            } else {
                alert("Failed to reset document.");
            }
            setResetModal({ isOpen: false, truckNo: null, docKey: null });
        } catch (error) {
            console.error('Error resetting document:', error);
            let errorMessage = 'Error resetting document';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            alert(errorMessage);
        }
    };

    return (
        <div id="vehicle">
            <form autoComplete="off" className="form-section" onSubmit={handleFormSubmit}>
                <div className="form-left">
                    <div className="filterItem">
                        <label htmlFor="truck_no">Truck No:</label>
                        <input
                            type="text"
                            id="truck_no"
                            name="truck_no"
                            value={formData.truck_no}
                            onChange={handleInputChange}
                            maxLength={10}
                            placeholder="e.g.: MH12KAB1111"
                            required
                        />
                        {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
                    </div>
                    <div className="filterItem">
                        <label htmlFor="make">Make (Truck Company):</label>
                        <input
                            type="text"
                            id="make"
                            name="make"
                            value={formData.make}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="filterItem">
                        <label htmlFor="companyOwner">Company Owner:</label>
                        <input
                            type="text"
                            id="companyOwner"
                            name="companyOwner"
                            value={formData.companyOwner}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="form-right">
                    <table className="field-table">
                        <tbody>
                            {formData.fields.map((field, index) => (
                                <React.Fragment key={index}>
                                    <tr>
                                        <td colSpan="3">
                                            <label htmlFor={`field-${index}`}>
                                                <strong>{keyDisplayMap[field.name] || field.name} Document:</strong>
                                            </label>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <input
                                                ref={(el) => (fileInputRef.current[index] = el)}
                                                type="file"
                                                id={`field-${index}`}
                                                onChange={(e) => handleFieldChange(index, "file", e.target.files[0])}
                                                accept=".pdf"
                                            />
                                            {fileErrors[`field-${index}`] && <p style={{ color: "red" }}>{fileErrors[`field-${index}`]}</p>}
                                            {progress[field.name] > 0 && (
                                                <div className="progress-bar">
                                                    <div className="progress-fill" style={{ width: `${progress[field.name]}%` }}></div>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <label htmlFor={`startDate-${index}`}>Start Date</label>
                                            <input
                                                type="date"
                                                id={`startDate-${index}`}
                                                value={field.startDate}
                                                onChange={(e) => handleFieldChange(index, "startDate", e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <label htmlFor={`endDate-${index}`}>End Date</label>
                                            <input
                                                type="date"
                                                id={`endDate-${index}`}
                                                value={field.endDate}
                                                onChange={(e) => handleFieldChange(index, "endDate", e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                    {dateError[`field-${index}`] && <p style={{ color: "red" }}>{dateError[`field-${index}`]}</p>}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button className="submit-button" type="submit">Submit</button>
                <p>{submitMessage}</p>
            </form>

            <div className="search-container">
                <input
                    type="text"
                    placeholder="Search by Truck Number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>
            <div className="tiles-view">
                {filteredVehicles.map((vehicle, index) => (
                    <div className="tile" key={index}>
                        <div className="tile-header">
                            <h3>{(vehicle.truck_no).split(' ').join('')}</h3>
                            <b>{vehicle.make || ""}</b>
                            <i>{vehicle.companyOwner || ""}</i>
                            <div className="tile-actions">
                                <button
                                    className="delete-button"
                                    onClick={() => handleDelete(vehicle.truck_no)}
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                        <hr />
                        <div className="tile-table">
                            <div className="tile-row tile-header-row">
                                <div className="tile-cell">Field</div>
                                <div className="tile-cell">Validity (days left)</div>
                                <div className="tile-cell">Download</div>
                            </div>
                            {result.length === 0 ? (
                                <tr>
                                    <td colSpan="5">No vehicle data found.</td>
                                </tr>
                            ) : (
                                Object.entries(vehicle.documents).map(([key, doc]) => (
                                    doc && (
                                        <div className="tile-row" key={key}>
                                            <div className="tile-cell">{keyDisplayMap[key] || key}</div>
                                            {!uploadFields[`${vehicle.truck_no}-${key}`] && activeUploadField !== `${vehicle.truck_no}-${key}` ? (
                                                <div className={`tile-cell ${doc.days_left <= 5 ? "highlighted" : ""}`}>
                                                    {doc.days_left <= 0 ? "(0 days left)" : `${formatDateDisplay(doc.end_date)} (${doc.days_left} days left)`}
                                                </div>
                                            ) : null}
                                            <div className="tile-cell">
                                                {doc.days_left <= 0 ? (
                                                    <>
                                                        {activeUploadField !== `${vehicle.truck_no}-${key}` ? (
                                                            <button
                                                                className="upload-button"
                                                                onClick={() => handleUploadClick(vehicle.truck_no, key)}
                                                            >
                                                                <FaUpload className="vehicle-icons" />
                                                            </button>
                                                        ) : (
                                                            <div className="upload-fields">
                                                                <div className="form-group">
                                                                    <label htmlFor={`${vehicle.truck_no}-${key}-file`}>File:</label>
                                                                    <input
                                                                        type="file"
                                                                        id={`${vehicle.truck_no}-${key}-file`}
                                                                        onChange={(e) => handleFileChange(e, vehicle.truck_no, key)}
                                                                    />
                                                                    {fileErrors[`field-${index}`] && <p style={{ color: "red" }}>{fileErrors[`field-${index}`]}</p>}
                                                                    {fileErrors[`${vehicle.truck_no}-${key}-file`] && (
                                                                        <p style={{ color: "red" }}>{fileErrors[`${vehicle.truck_no}-${key}-file`]}</p>
                                                                    )}
                                                                </div>
                                                                <div className="form-group">
                                                                    <label htmlFor={`${vehicle.truck_no}-${key}-start`}>Start Date:</label>
                                                                    <input
                                                                        type="date"
                                                                        id={`${vehicle.truck_no}-${key}-start`}
                                                                        defaultValue={startDate}
                                                                        onChange={(e) => handleStartDateChange(e, endDate, vehicle.truck_no, key)}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label htmlFor={`${vehicle.truck_no}-${key}-end`}>End Date:</label>
                                                                    <input
                                                                        type="date"
                                                                        id={`${vehicle.truck_no}-${key}-end`}
                                                                        defaultValue={endDate}
                                                                        onChange={(e) => handleEndDateChange(e, startDate, vehicle.truck_no, key)}
                                                                    />
                                                                </div>
                                                                {dateError[`${vehicle.truck_no}-${key}`] && (
                                                                    <p style={{ color: "red" }}>{dateError[`${vehicle.truck_no}-${key}`]}</p>
                                                                )}
                                                                <button
                                                                    className="save-button"
                                                                    onClick={() => {
                                                                        handleSaveClick(vehicle.truck_no, key);
                                                                    }}
                                                                >
                                                                    Save
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="button-group">
                                                        <button
                                                            className="download-button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                // Create a hidden anchor element
                                                                const link = document.createElement('a');
                                                                link.href = `${config.BASE_URL}/download/${vehicle.truck_no}/${key}`;
                                                                link.download = true;
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                document.body.removeChild(link);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <FaDownload className="vehicle_icons" />
                                                        </button>
                                                        <button
                                                            className="reset-button"
                                                            onClick={() => handleResetClick(vehicle.truck_no, key)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <FaUndo className="vehicle_icons" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {progress?.[`${vehicle.truck_no}-${key}`] > 0 && (
                                                <div className="progress-bar">
                                                    <div
                                                        className="progress-fill"
                                                        style={{
                                                            width: `${progress[`${vehicle.truck_no}-${key}`] || 0}%`,
                                                            transition: "width 0.3s ease-in-out",
                                                            backgroundColor: "green",
                                                            height: "5px"
                                                        }}
                                                    ></div>
                                                </div>
                                            )}
                                        </div>
                                    )
                                ))
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {deleteModal.isOpen && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal">
                        <div className="delete-modal-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="54" height="54">
                                <path fill="none" d="M0 0h24v24H0z" />
                                <path fill="#ff4757" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z" />
                            </svg>
                        </div>
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete this Vehicle: <span className="trip-id-highlight">{deleteModal.recordId}</span>?</p>
                        <p className="warning-text">This action cannot be undone.</p>
                        <div className="delete-modal-buttons">
                            <button className="cancel-btn" onClick={() => setDeleteModal({ isOpen: false, recordId: null })}>
                                Cancel
                            </button>
                            <button className="confirm-delete-btn" onClick={confirmDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Reset modale */}
            {resetModal.isOpen && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal">
                        <div className="delete-modal-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="54" height="54">
                                <path fill="none" d="M0 0h24v24H0z" />
                                <path fill="#ff9800" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z" />
                            </svg>
                        </div>
                        <h3>Confirm Reset</h3>
                        <p>Are you sure you want to reset {keyDisplayMap[resetModal.docKey] || resetModal.docKey} document for vehicle: <span className="trip-id-highlight">{resetModal.truckNo}</span>?</p>
                        <p className="warning-text">This will delete the document and set validity to 0 days.</p>
                        <div className="delete-modal-buttons">
                            <button className="cancel-btn" onClick={() => setResetModal({ isOpen: false, truckNo: null, docKey: null })}>
                                Cancel
                            </button>
                            <button className="confirm-delete-btn" onClick={confirmReset}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vehicle;